import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import { Box, IconButton, Tooltip, useTheme } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AuthorizationProvider } from 'src/components/common/Authorization'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { CreateCertificateExtensionDialog } from 'src/components/main-component/CreateCertificateExtensionDialog'
import { AuthorizationType } from 'src/enums/auth'
import { IMainComponentType as Type } from 'src/enums'
import { permissionValue } from 'src/enums/permissionValue'
import { ICertificate } from 'src/generated-types/Certificate'
import { IMainComponent } from 'src/generated-types/main-component'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor, setToMidnight } from 'src/utils'
import { sortCertificates } from 'src/utils/function'
import { ReactComponent as PlusTimerOutlined } from '../../../../assets/svg/PlusTimerOutlined.svg'
import { ReactComponent as CertificateIcon } from '../../../../assets/icons/certificate.svg'
import { CreateCertificateDialog } from '../../Certificates/Components/CertificatesContainer'

interface CertificateComponentProps {
  certificates: ICertificate[]
  mainComponent: IMainComponent
  customerId: EntityId
  context?: React.Context<any> | null
  hasPendingService?: boolean
  disabledAddButton?: boolean
}

const CertificateComponent = ({
  certificates,
  mainComponent,
  customerId,
  context,
  hasPendingService,
  disabledAddButton,
}: CertificateComponentProps) => {
  const sortedCertificates = useMemo(() => {
    const sorted = sortCertificates(certificates || [])

    return sorted.map((cert, index) => ({
      ...cert,
      isCurrentlyActive:
        index === 0 &&
        cert.isValid === true &&
        (() => {
          const validUntil = cert?.validUntil?.value ?? cert?.validUntil
          const expiryDate = validUntil ? new Date(validUntil).getTime() : Number.MAX_SAFE_INTEGER
          const now = setToMidnight(new Date()).getTime()
          return expiryDate >= now
        })(),
    }))
  }, [certificates])

  const activeCertificate = sortedCertificates.find(cert => cert.isCurrentlyActive)

  const currentCertificate = activeCertificate ?? null
  const { showDialog, openFilePreviewDialog } = useDialogCtx()
  const { t } = useTranslation(['document', 'common', 'certificate', 'main-component', 'certificateExtension'])
  const [disableAddCertificate, setDisableAddCertificate] = useState(false)

  const theme = useTheme()

  const onAddCertificateForComponent = useCallback(async () => {
    const added = await showDialog(
      CreateCertificateDialog,
      {
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        componentProps: {
          mainComponentId: mainComponent.id as EntityId,
          customerId: customerId as EntityId,
        },
      },
      undefined,
      true,
    )
    if (!added) return
  }, [customerId, mainComponent, showDialog])

  const onAddExtension = async (id: EntityId) => {
    const added = await showDialog(CreateCertificateExtensionDialog, {
      title: t('addExtension', { ns: 'certificateExtension' }),
      componentProps: {
        certificateId: id as EntityId,
        mainComponentId: mainComponent.id as EntityId,
        customerId: customerId as EntityId,
      },
      opts: {
        maxWidth: 'md',
        fullWidth: true,
      },
    })
    if (!added) return
  }

  const isDisable = (expiresIn: number, numberOfExtensions: number) => {
    return expiresIn > 92 || numberOfExtensions >= 2
  }

  useEffect(() => {
    if (hasPendingService) {
      setDisableAddCertificate(hasPendingService)
    }
  }, [hasPendingService])

  return (
    <Flex.Row gap={0.5} justifyContent={'center'} alignItems={'center'}>
      <Box display={'flex'} alignItems={'center'} gap={0.5} flexDirection={'row'}>
        <CertificateIcon
          width="16px"
          height="16px"
          style={{
            color: currentCertificate
              ? getThemeColor(theme, MUIThemeColors.primaryMain)
              : getThemeColor(theme, MUIThemeColors.secondaryMain),
          }}
        />
        <DynamicTextTypography
          text={
            currentCertificate
              ? `${t('certified', { ns: 'certificate' })}: ${currentCertificate.certificateNumber}`
              : t('not-certified', { ns: 'certificate' })
          }
          color={
            currentCertificate
              ? getThemeColor(theme, MUIThemeColors.primaryMain)
              : getThemeColor(theme, MUIThemeColors.secondaryMain)
          }
          fontSize={'13px'}
          fontWeight={400}
          sx={{
            textDecoration: currentCertificate ? 'underline' : 'none',
            cursor: currentCertificate ? 'pointer' : 'default',
          }}
          onClick={() => currentCertificate && openFilePreviewDialog(currentCertificate.fileUrl)}
        />
      </Box>
      {currentCertificate && mainComponent?.type?.componentType == Type.Net && (
        <Tooltip title={t('addExtension', { ns: 'certificateExtension' }) as string}>
          <IconButton
            onClick={() =>
              !isDisable(mainComponent?.expires ?? 0, currentCertificate?.numberOfExtensions) &&
              onAddExtension(currentCertificate?.id as EntityId)
            }
            disabled={
              isDisable(mainComponent?.expires ?? 0, currentCertificate?.numberOfExtensions) ||
              !currentCertificate?.isCurrentlyActive
            }
            size="small"
            sx={{ marginTop: '-3.5px', color: getThemeColor(theme, MUIThemeColors.primaryMain) }}
          >
            <PlusTimerOutlined height="17px" width="17px" />
          </IconButton>
        </Tooltip>
      )}
      <AuthorizationProvider
        permissions={[
          permissionValue.Locality.MainComponent.Certificate.Write,
          permissionValue.ClientAdminPermission,
          permissionValue.ServiceStation.Write,
          permissionValue.ClientAdminPortal.Equipment_Portal,
        ]}
        Context={context}
        type={AuthorizationType.Disabled}
      >
        <Tooltip title={t('add-certificate', { ns: 'certificate' }) as string}>
          <IconButton
            onClick={onAddCertificateForComponent}
            disabled={disabledAddButton != undefined ? disabledAddButton : disableAddCertificate}
            size="small"
            sx={{ color: getThemeColor(theme, MUIThemeColors.primaryMain) }}
          >
            <AddCircleOutlineOutlinedIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Tooltip>
      </AuthorizationProvider>
    </Flex.Row>
  )
}
export default CertificateComponent
